import * as AdaptiveCards from "adaptivecards";
import * as ACData from "adaptivecards-templating";
import MarkdownIt from "markdown-it";
import DateHelper from "../common/DateHelper";

export abstract class AdaptiveCardHelper {
  public static renderAdaptiveCard = (
    elementId: string,
    adaptiveCardTemplate: any,
    adaptiveCardData: any
  ) => {
    adaptiveCardData = this.formatCallerDetailsDateTime(adaptiveCardData);

    // Render the card
    const adaptiveCard = new AdaptiveCards.AdaptiveCard();

    AdaptiveCards.AdaptiveCard.onProcessMarkdown = function (text, result) {
      const md = new MarkdownIt();
      result.outputHtml = md.render(text);
      result.didProcess = true;
    };

    // Set its hostConfig property unless you want to use the default Host Config
    // Host Config defines the style and behavior of a card
    adaptiveCard.hostConfig = new AdaptiveCards.HostConfig({
      fontFamily: "Segoe UI, Helvetica Neue, sans-serif",
    });

    // Create a Template instance from the template payload
    const template = new ACData.Template(adaptiveCardTemplate);

    const evaluationContext: ACData.IEvaluationContext = {
      $root: {},
    };

    evaluationContext.$root = adaptiveCardData;
    const card = template.expand(evaluationContext);

    adaptiveCard.parse(card);

    // Render the card to an HTML element:
    // eslint-disable-next-line
    const renderedCard = adaptiveCard.render();

    const elementDiv = document.getElementById(elementId);
    if (elementDiv && renderedCard) {
      while (elementDiv.firstChild) {
        if (elementDiv.lastChild) {
          elementDiv.removeChild(elementDiv.lastChild);
        }
      }

      elementDiv.appendChild(renderedCard);
    }

    return { adaptiveCard };
  };

  public static isStepCompleted = (
    currentStepId: string,
    adaptiveCardData: any,
    actionData: any
  ) => {
    const stepFields = Object.keys(adaptiveCardData["tasks"][currentStepId]);
    let isStepCompleted = true;

    stepFields.forEach((stepField: string) => {
      if (stepField !== "status") {
        const stepFieldJson =
          adaptiveCardData["tasks"][currentStepId][stepField];
        const stepFieldId = stepFieldJson["id"];
        const isStepFieldMandatory = JSON.parse(
          stepFieldJson["isRequired"] ?? false
        );

        if (isStepFieldMandatory && actionData[stepFieldId] === "") {
          isStepCompleted = false;
        }
      }
    });

    return isStepCompleted;
  };

  public static disableAdaptiveCardFields = (taskFields: {
    disableIdArray: any[];
    disableSaveArray: any[];
    totalFields: number;
  }) => {
    taskFields.disableIdArray?.forEach((fieldIdToDisable: string) => {
      const divElement = document.getElementById(fieldIdToDisable);

      if (divElement) {
        const divFields = divElement.getElementsByTagName("*");
        Array.from(divFields).forEach((divField: any) => {
          divField.setAttribute("disabled", "true");
        });
      }
    });

    //Disable Save button if any field is disabled
    if (taskFields.disableSaveArray.length > 0) {
      const saveButtons = document.querySelectorAll('[id^="step"][id$="save"]');
      saveButtons.forEach((saveButton: any) => {
        const buttonFields = saveButton.getElementsByTagName("button");
        Array.from(buttonFields).forEach((buttonField: any) => {
          buttonField.setAttribute("disabled", "true");
          buttonField.classList?.add("ac-pushButton-disabled");
          buttonField.classList?.remove("ac-pushButton");
        });
      });
    }
  };

  public static getTaskFields = (
    userRoles: any,
    adaptiveCardData: any,
    makeFieldNonMandatory = false
  ) => {
    const fields: any = {
      disableIdArray: [],
      disableSaveArray: [],
      totalFields: 0,
    };
    const steps = Object.keys(adaptiveCardData["tasks"]);
    steps.forEach((step: string) => {
      let shouldDisableSaveButton = false;
      const stepJson = adaptiveCardData["tasks"][step];
      const stepFields = Object.keys(stepJson);

      stepFields.forEach((stepField: any) => {
        if (stepField !== "status" && stepField !== "metadata") {
          const filteredUserRoles = userRoles.filter((userRole: any) => {
            return userRole.roleId === stepJson[stepField].roleId;
          });

          if (userRoles.length === 0 || filteredUserRoles.length === 0) {
            fields.disableIdArray.push(stepJson[stepField]["id"]);
            shouldDisableSaveButton = true;
          }
          fields.totalFields += 1;
        }
      });

      //add save button id to disableSaveArray
      if (shouldDisableSaveButton) {
        fields.disableSaveArray.push(`${step}.save`);
      }
    });

    if (makeFieldNonMandatory) {
      this.markFieldsNonMandatory(adaptiveCardData, fields.disableIdArray);
    }

    return fields;
  };

  public static markFieldsNonMandatory = (
    adaptiveCardData: any,
    fieldsToDisable: string[]
  ) => {
    fieldsToDisable.forEach((fieldId: string) => {
      const fieldArray = fieldId.split(".");
      if (fieldArray.length === 3) {
        adaptiveCardData[fieldArray[0]][fieldArray[1]][fieldArray[2]][
          "isRequired"
        ] = false;
      }
    });
  };

  public static markStepCompletion = (
    currentStepNumber: string,
    nextStepNumber: string,
    adaptiveCardData: any,
    action: any
  ) => {
    const isStepCompleted = AdaptiveCardHelper.isStepCompleted(
      currentStepNumber,
      adaptiveCardData,
      action?.data
    );

    Object.keys(action?.data).forEach((key) => {
      if (key.indexOf("tasks.") > -1 || key.indexOf("decision.") > -1) {
        const val = action?.data[key];
        delete action?.data[key];
        action.data[`${key}.value`] = val;
      }
    });

    if (isStepCompleted) {
      action.data["completedStepNumber"] = currentStepNumber;
      action.data["nextStepNumber"] = nextStepNumber;
      action.data[`tasks.${currentStepNumber}.metadata.completed`] = true;
    }
  };

  private static readonly formatCallerDetailsDateTime = (
    adaptiveCardData: any
  ) => {
    adaptiveCardData.details.caller_details.date =
      DateHelper.getFormattedDateTime(
        new Date(adaptiveCardData.details.caller_details.date),
        "YYYY-MM-DD"
      );

    const callerDetailsTime = adaptiveCardData.details.caller_details.time;
    adaptiveCardData.details.caller_details.time = DateHelper.isValidDate(
      callerDetailsTime
    )
      ? DateHelper.getFormattedDateTime(new Date(callerDetailsTime), "HH:mm")
      : callerDetailsTime;

    return adaptiveCardData;
  };
}
